import moment from 'moment';

/** 'YYYY-MM-DDTHH:mm' (or Date/ISO) → ISO-8601 UTC (interprets the wall-clock as UTC) */
export function toUtcIso(v: string | Date): string {
  return moment.utc(v).toISOString();
}

/** ISO instant → 'YYYY-MM-DDTHH:mm' in UTC wall-clock */
export function utcIsoToDatetimeLocal(value: string | Date | null | undefined): string | undefined {
  if (!value) return undefined;
  const m = moment.utc(value);
  return m.isValid() ? m.format('YYYY-MM-DDTHH:mm') : undefined;
}

/**
 * Offset (in minutes) of the IANA `timeZone` relative to UTC at the given instant.
 * Positive = zone ahead of UTC. Uses Intl (no moment-timezone dependency).
 */
function zoneOffsetMinutes(utcDate: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const map: Record<string, number> = {};
  for (const p of dtf.formatToParts(utcDate)) {
    if (p.type !== 'literal') map[p.type] = Number(p.value);
  }
  const asUtc = Date.UTC(map['year'], map['month'] - 1, map['day'], map['hour'], map['minute'], map['second']);
  return Math.round((asUtc - utcDate.getTime()) / 60000);
}

/**
 * Interpret a wall-clock 'YYYY-MM-DDTHH:mm' as local time in IANA `timeZone`
 * and convert it to a UTC ISO-8601 string.
 * If `timeZone` is missing or 'UTC' → same behaviour as toUtcIso.
 */
export function zonedWallClockToUtcIso(v: string | Date, timeZone: string): string {
  if (!timeZone || timeZone === 'UTC') return moment.utc(v).toISOString();

  const guess = moment.utc(v);
  if (!guess.isValid()) return moment.utc(v).toISOString();

  const wallAsUtcMs = guess.valueOf();                 // wall-clock read as a UTC instant
  let offset = zoneOffsetMinutes(new Date(wallAsUtcMs), timeZone);
  let utcMs = wallAsUtcMs - offset * 60000;            // first estimate
  offset = zoneOffsetMinutes(new Date(utcMs), timeZone);
  utcMs = wallAsUtcMs - offset * 60000;               // one refinement (handles DST edges)

  return moment.utc(utcMs).toISOString();
}
